/**
 * Shared constants and base class for Typesense collection v2.
 */

const SORT_OPTIONS = {
  PRICE_LOW_TO_HIGH: 'PRICE_LOW_TO_HIGH',
  PRICE_HIGH_TO_LOW: 'PRICE_HIGH_TO_LOW',
  ADDED_NEW_TO_OLD: 'ADDED_NEW_TO_OLD',
  ADDED_OLD_TO_NEW: 'ADDED_OLD_TO_NEW',
  PRICE_DROP_RECENT: 'PRICE_DROP_RECENT',
  PRICE_DROP_BIGGEST: 'PRICE_DROP_BIGGEST',
  PRICE_DROP_PERCENTAGE: 'PRICE_DROP_PERCENTAGE',
  LATEST_ACTIVITY_NEWEST: 'LATEST_ACTIVITY_NEWEST',
  MOST_FAVOURITED: 'MOST_FAVOURITED'
};

const SORT_LABELS = {
  [SORT_OPTIONS.PRICE_LOW_TO_HIGH]: 'Low to High',
  [SORT_OPTIONS.PRICE_HIGH_TO_LOW]: 'High to Low',
  [SORT_OPTIONS.ADDED_NEW_TO_OLD]: 'Newest First',
  [SORT_OPTIONS.ADDED_OLD_TO_NEW]: 'Oldest First',
  [SORT_OPTIONS.PRICE_DROP_RECENT]: 'Most Recently Reduced',
  [SORT_OPTIONS.PRICE_DROP_BIGGEST]: 'Biggest Price Drop (£)',
  [SORT_OPTIONS.PRICE_DROP_PERCENTAGE]: 'Biggest Price Drop (%)',
  [SORT_OPTIONS.LATEST_ACTIVITY_NEWEST]: 'Recently Listed or Reduced',
  [SORT_OPTIONS.MOST_FAVOURITED]: 'Most Favourited'
};

const SORT_GROUPS = [
  {
    id: 'latest_activity',
    label: 'LATEST ACTIVITY',
    tooltip: 'Sort by most recent activity (price drop or date added)',
    optionKeys: [SORT_OPTIONS.LATEST_ACTIVITY_NEWEST]
  },
  {
    id: 'price_drop',
    label: 'PRICE DROP',
    tooltip: 'Sort by products with recent price reductions or biggest price drops',
    optionKeys: [
      SORT_OPTIONS.PRICE_DROP_RECENT,
      SORT_OPTIONS.PRICE_DROP_BIGGEST,
      SORT_OPTIONS.PRICE_DROP_PERCENTAGE
    ]
  },
  {
    id: 'price',
    label: 'PRICE',
    tooltip: 'Sort by current product price',
    optionKeys: [SORT_OPTIONS.PRICE_LOW_TO_HIGH, SORT_OPTIONS.PRICE_HIGH_TO_LOW]
  },
  {
    id: 'date_added',
    label: 'DATE ADDED',
    tooltip: 'Sort by when products were first listed',
    optionKeys: [SORT_OPTIONS.ADDED_NEW_TO_OLD, SORT_OPTIONS.ADDED_OLD_TO_NEW]
  },
  {
    id: 'popular',
    label: 'POPULAR',
    tooltip: 'Sort by how many customers have favourited each product',
    optionKeys: [SORT_OPTIONS.MOST_FAVOURITED]
  }
];

const CANONICAL_FILTERS = [
  { id: 'brand', label: 'Brand' },
  { id: 'gender', label: 'Gender' },
  { id: 'category', label: 'Category' },
  { id: 'product_type', label: 'Product Type' },
  { id: 'size', label: 'Size' },
  { id: 'colour', label: 'Colour' },
  { id: 'price', label: 'Price' },
  { id: 'condition', label: 'Condition' }
];

const FILTER_DISPLAY_ORDER = CANONICAL_FILTERS.map(f => f.id);

const FILTER_ID_ALIASES = {
  color: 'colour',
  vendor: 'brand',
  sub_category: 'product_type',
  producttype: 'product_type',
  'size.facet_key': 'size',
  'size.value_facet': 'size'
};

function normalizeFilterIdAlias(rawId) {
  const normalized = (rawId || '').toLowerCase();
  return FILTER_ID_ALIASES[normalized] || normalized;
}

function formatCanonicalFilterLabel(filterId, fallbackLabel) {
  const match = CANONICAL_FILTERS.find(f => f.id === filterId);
  if (match) return match.label;
  return fallbackLabel || filterId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

class CollectionAdapterV2 {
  constructor(config) {
    this.config = config || {};
  }

  getSortOptionsMapping() {
    throw new Error('Must implement getSortOptionsMapping()');
  }

  isSortSupported(sortId, context = 'collection') {
    return this.getSortOptionsMapping({ context })[sortId] != null;
  }

  toCanonicalSortId(value, context = 'collection') {
    if (!value) return null;
    const mapping = this.getSortOptionsMapping({ context });
    if (mapping[value] != null) return value;
    const match = Object.entries(mapping).find(([, adapterValue]) => adapterValue === value);
    return match ? match[0] : null;
  }

  buildSortOptions(currentSortBy, context = 'collection') {
    const mapping = this.getSortOptionsMapping({ context });
    return Object.entries(mapping)
      .filter(([, value]) => value !== null)
      .map(([key, value]) => ({
        key,
        value,
        label: SORT_LABELS[key] || key,
        group: SORT_GROUPS.find(g => g.optionKeys.includes(key))?.label || '',
        active: key === currentSortBy
      }));
  }

  buildSortGroups(currentSort, context = 'collection') {
    const mapping = this.getSortOptionsMapping({ context });
    return SORT_GROUPS.map(group => {
      const options = group.optionKeys
        .filter(key => mapping[key] != null)
        .map(key => ({
          key,
          label: SORT_LABELS[key],
          active: key === currentSort
        }));
      if (!options.length) return null;
      return {
        id: group.id,
        label: group.label,
        tooltip: group.tooltip,
        options
      };
    }).filter(Boolean);
  }

  resolveProductsCount({ total, products = [] }) {
    if (typeof total === 'number' && !Number.isNaN(total)) return total;
    return products.length;
  }

  isFilterValueSelected(filterId, value, activeFilters = {}) {
    const filterValues = activeFilters[filterId];
    if (!filterValues) return false;
    if (Array.isArray(filterValues)) return filterValues.includes(value);
    return filterValues === value;
  }

  mergeCatalogValues(filterId, baselineValues, incomingValues, activeFilters = {}) {
    const incomingByValue = new Map((incomingValues || []).map(value => [value.value, value]));
    const merged = [];
    const seen = new Set();

    (baselineValues || []).forEach(baselineValue => {
      seen.add(baselineValue.value);
      const incomingValue = incomingByValue.get(baselineValue.value);
      merged.push({
        ...baselineValue,
        ...incomingValue,
        count: incomingValue?.count ?? 0,
        active: this.isFilterValueSelected(filterId, baselineValue.value, activeFilters)
      });
    });

    (incomingValues || []).forEach(incomingValue => {
      if (seen.has(incomingValue.value)) return;
      merged.push({
        ...incomingValue,
        active: this.isFilterValueSelected(filterId, incomingValue.value, activeFilters)
      });
    });

    return merged;
  }

  mergeCatalogGroups(filterId, baselineGroups, incomingGroups, incomingValues, activeFilters = {}) {
    if (!baselineGroups?.length) return incomingGroups;

    const incomingByName = new Map((incomingGroups || []).map(group => [group.name, group]));
    const incomingValueMap = new Map((incomingValues || []).map(value => [value.value, value]));
    const merged = [];
    const seen = new Set();

    baselineGroups.forEach(baselineGroup => {
      seen.add(baselineGroup.name);
      const incomingGroup = incomingByName.get(baselineGroup.name);
      const incomingGroupValues = incomingGroup?.values
        || (baselineGroup.values || []).map(value => incomingValueMap.get(value.value)).filter(Boolean);

      merged.push({
        ...baselineGroup,
        values: this.mergeCatalogValues(filterId, baselineGroup.values, incomingGroupValues, activeFilters)
      });
    });

    (incomingGroups || []).forEach(incomingGroup => {
      if (seen.has(incomingGroup.name)) return;
      merged.push(incomingGroup);
    });

    return merged;
  }

  mergeDisjunctiveFacetCounts(mainFacets, disjunctiveFacetsByField) {
    const merged = [...(mainFacets || [])];
    Object.entries(disjunctiveFacetsByField || {}).forEach(([fieldName, facetData]) => {
      if (!facetData) return;
      const index = merged.findIndex(facet => facet.field_name === fieldName);
      if (index >= 0) {
        merged[index] = {
          ...merged[index],
          ...facetData,
          field_name: fieldName,
          counts: facetData.counts ?? merged[index].counts,
          stats: facetData.stats ?? merged[index].stats
        };
      } else {
        merged.push({ ...facetData, field_name: fieldName });
      }
    });
    return merged;
  }

  preserveFilterCatalog(baselineFilters, incomingFilters, activeFilters = {}) {
    if (!baselineFilters?.length) return incomingFilters || [];

    const incomingById = new Map((incomingFilters || []).map(filter => [filter.id, filter]));
    const merged = [];
    const handledIds = new Set();

    baselineFilters.forEach(baselineFilter => {
      handledIds.add(baselineFilter.id);
      const incoming = incomingById.get(baselineFilter.id);

      if (baselineFilter.type === 'price_range') {
        merged.push(incoming ? { ...baselineFilter, ...incoming } : { ...baselineFilter });
        return;
      }

      if (!incoming) {
        merged.push({
          ...baselineFilter,
          values: this.mergeCatalogValues(baselineFilter.id, baselineFilter.values, [], activeFilters),
          groups: baselineFilter.groups?.length
            ? this.mergeCatalogGroups(baselineFilter.id, baselineFilter.groups, [], [], activeFilters)
            : baselineFilter.groups
        });
        return;
      }

      const mergedValues = this.mergeCatalogValues(
        baselineFilter.id,
        baselineFilter.values,
        incoming.values,
        activeFilters
      );
      const mergedGroups = baselineFilter.groups?.length || incoming.groups?.length
        ? this.mergeCatalogGroups(
            baselineFilter.id,
            baselineFilter.groups,
            incoming.groups,
            incoming.values,
            activeFilters
          )
        : incoming.groups;
      const resolvedType = mergedGroups?.length ? 'grouped_option' : (incoming.type || baselineFilter.type);

      merged.push({
        ...baselineFilter,
        ...incoming,
        type: resolvedType,
        values: mergedValues,
        groups: mergedGroups
      });
    });

    (incomingFilters || []).forEach(incomingFilter => {
      if (!handledIds.has(incomingFilter.id)) merged.push(incomingFilter);
    });

    return merged;
  }

  sortFiltersByDisplayOrder(filters = []) {
    const orderIndex = new Map(FILTER_DISPLAY_ORDER.map((id, index) => [id, index]));
    const fallback = FILTER_DISPLAY_ORDER.length;

    return [...filters].sort((a, b) => {
      const aIdx = orderIndex.has(a.id) ? orderIndex.get(a.id) : fallback;
      const bIdx = orderIndex.has(b.id) ? orderIndex.get(b.id) : fallback;
      return aIdx - bIdx;
    });
  }

  buildCollectionResult(payload) {
    const products = payload.products || [];
    let filters = payload.filters || [];

    if (payload.baselineFilters?.length) {
      filters = this.preserveFilterCatalog(
        payload.baselineFilters,
        filters,
        payload.activeFiltersForCatalog || {}
      );
    }

    filters = this.sortFiltersByDisplayOrder(filters);

    const sortContext = payload.sortContext || 'collection';
    const sortGroups = this.buildSortGroups(payload.currentSort || null, sortContext);

    return {
      products,
      filters,
      active_filters: payload.active_filters || [],
      sort_options: payload.sort_options || [],
      sort_groups: sortGroups,
      products_count: products.length,
      products_count_total: this.resolveProductsCount({
        total: payload.products_count_total,
        products
      }),
      pagination: payload.pagination,
      current_url: payload.current_url,
      ...(payload.search_query ? { search_query: payload.search_query } : {})
    };
  }

  getEmptyState() {
    return {
      products: [],
      filters: [],
      active_filters: [],
      sort_options: [],
      sort_groups: [],
      products_count: 0,
      products_count_total: 0,
      pagination: {
        current_page: 1,
        total_pages: 0,
        per_page: 20,
        has_next: false,
        has_previous: false,
        next_url: null,
        previous_url: null
      },
      current_url: typeof window !== 'undefined' ? window.location.pathname : ''
    };
  }
}

if (typeof window !== 'undefined') {
  window.CollectionAdapterV2 = CollectionAdapterV2;
  window.SORT_OPTIONS = SORT_OPTIONS;
  window.SORT_LABELS = SORT_LABELS;
  window.SORT_GROUPS = SORT_GROUPS;
  window.CANONICAL_FILTERS = CANONICAL_FILTERS;
  window.FILTER_DISPLAY_ORDER = FILTER_DISPLAY_ORDER;
  window.normalizeFilterIdAlias = normalizeFilterIdAlias;
  window.formatCanonicalFilterLabel = formatCanonicalFilterLabel;
}
